import { useState, useEffect, useRef } from "react";
import { useCustomLocaleText } from "@/Components/Common/useCustomLocaleText";
import TableAction from "@/Components/Common/TablePro/TableAction";
import {
  Avatar,
  Box,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  ClickAwayListener,
  Button,
  IconButton,
  Modal,
  Paper,
} from "@mui/material";
import { DataGridPro, GridColDef, GridOverlay } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import { AnyObject } from "yup";
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  ChatBubbleOutline,
  PersonAddOutlined,
  CalendarToday,
  Add,
} from "@mui/icons-material";
import {
  colorPalette,
  getInitials,
  severityColorMap,
  typeToIconMapping,
} from "../utils";
// import TaskUpdateModal from "@/layouts/rightSideBar/BugHurd/components/TaskUpdateModal";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import HighestIcon from "@/assets/icons/highest.svg?react";
import HighIcon from "@/assets/icons/high.svg?react";
import MediumIcon from "@/assets/icons/medium.svg?react";
import LowIcon from "@/assets/icons/low.svg?react";
import {
  useGetListOfStatusQuery,
  useUpdateBugDetailMutation,
  useGetBugDetailByBugIdQuery,
  useLazyGetBugDetailByBugIdQuery,
  useDeleteBugMutation,
  useCreateBugMutation,
  useUpdateBugIndexMutation,
} from "@/features/bughurd/api";
import EditPopover from "./EditPopover";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import { toast } from "react-toastify";
import Pagination from "@/Components/Common/TablePro/Pagination";
import TaskUpdateDrawer from "@/pages/Board/bugherdComponents/TaskUpdateDrawer";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import moment from "moment";
import { useTaskOperations } from "./custom-hooks/useTaskOperations";
import { useBoardInitialization } from "./custom-hooks/useBoardInitialization";
import {
  setCreateTask,
  setSelectedColumnStatus,
} from "@/features/bughurd/slice";
import { selectGroupingConfig } from "@/features/bughurd/boardSelector";

// Fake row ID for the "Add Subtask" row
const ADD_SUBTASK_ROW_ID = "add-subtask-row";

const NestedTaskList = ({
  currentRow,
  handleAction,
  type = "task",
  pagination,
  handlePageChange,
  loadingStatuses,
  projectDetails,
}: {
  currentRow: any;
  handleAction: any;
  type: string;
  pagination: Record<string, any>;
  projectDetails: any;
  handlePageChange: (
    columnId: string,
    paginationModel: any,
    viewType: string
  ) => void;
  loadingStatuses: any;
}) => {
  const { groupBy } = useAppSelector(selectGroupingConfig);
  const customLocaleText = useCustomLocaleText();
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bugId = searchParams.get("bugId");
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { dateFormat } = useAppSelector((state) => state.appGlobal);

  const { isMyTaskView } = useBoardInitialization(id);
  const { refreshColumnData } = useTaskOperations(id, isMyTaskView);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: pagination[currentRow?.id]?.limit || 10,
    page: 1,
  });

  // For inline editing
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [selectedAssignees, setSelectedAssignees] = useState([]);

  // For inline name editing
  const [editingNameId, setEditingNameId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const nameInputRef = useRef(null);

  // For add subtask functionality
  const [isAddingSubtask, setIsAddingSubtask] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const { columns: columnData } = useAppSelector((state) => state.board);
  // For delete functionality
  const [isDeleteReply, setIsDeleteReply] = useState(false);
  const [bugToDelete, setBugToDelete] = useState(null);
  const [deleteBug, { isLoading: isDeleteLoading }] = useDeleteBugMutation();
  const [createBug, { isLoading: isCreatingBug }] = useCreateBugMutation();
  const [subTaskData, setSubTaskData] = useState([]);

  // For fetching status labels and updating bugs
  const { data: statusLists, isLoading: isLoadingStatusLabel } =
    useGetListOfStatusQuery(
      { projectId: id },
      {
        skip: !id,
      }
    );

  // API hook for updating bugs
  const [updateBug, { isLoading: updatingBug }] = useUpdateBugDetailMutation();

  // Get bug details for subtasks (non-editing)
  const {
    data: subtaskData,
    isLoading: loadingSubtask,
    isFetching: fetchingSubtask,
    refetch: refetchSubtask,
  } = useGetBugDetailByBugIdQuery(
    {
      bugId: type === "subTask" ? currentRow._id : undefined,
      withDetails: true,
    },
    {
      skip: type !== "subTask",
      refetchOnMountOrArgChange: true,
    }
  );

  const [updateBugIndex] = useUpdateBugIndexMutation();

  // Lazy query for editing tasks
  const [
    fetchBugDetail,
    { data: editingBugDetail, isFetching: isFetchingEditingBug },
  ] = useLazyGetBugDetailByBugIdQuery();

  useEffect(() => {
    if (type === "subTask") {
      if (subtaskData?.data?.decryptedData?.subTasks) {
        setSubTaskData(subtaskData.data.decryptedData.subTasks);
      }
    }
  }, [subtaskData, type]);

  const availableStatus = statusLists?.data?.decryptedData?.items || [];
  const { data: companyMemberData } = useGetCompanyMembersQuery(1);
  const users = companyMemberData?.data?.decryptedData || [];

  // Get available item types from project details
  const availableItemTypes = projectDetails?.itemTypes || [];
  // Base list data without the Add Subtask row
  const baseListData = type === "task" ? currentRow?.tasks ?? [] : subTaskData;

  // Add the "Add Item" row for both tasks and subtasks
  const listData = [...baseListData, { _id: ADD_SUBTASK_ROW_ID, summary: "" }];

  const hasSubtasks = (row) => {
    return row?.subTasksCount > 0;
  };

  const shouldShowDetailPanel = (row = {}) => {
    return true;
  };

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (editingNameId && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [editingNameId]);

  const severityOptions = [
    { value: "Highest", label: t("Highest"), icon: <HighestIcon /> },
    { value: "High", label: t("High"), icon: <HighIcon /> },
    { value: "Medium", label: t("Medium"), icon: <MediumIcon /> },
    { value: "Low", label: t("Low"), icon: <LowIcon /> },
  ];

  const getSeverityIcon = (severity) => {
    const style = {
      minWidth: "auto",
      minHeight: "auto",
      width: 20,
      height: 20,
    };
    switch (severity) {
      case "Highest":
        return <HighestIcon style={style} />;
      case "High":
        return <HighIcon style={style} />;
      case "Medium":
        return <MediumIcon style={style} />;
      case "Low":
        return <LowIcon style={style} />;
      default:
        return null;
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedTaskId(null);
    if (bugId) {
      navigate(pathname);
    }
  };

  const handleOpenModal = (taskId) => {
    if (type === "subTask" || taskId === ADD_SUBTASK_ROW_ID) return;
    setSelectedTaskId(taskId);
    setIsModalOpen(true);
    navigate(pathname + "?bugId=" + taskId);
  };

  const handleAddSubtask = async () => {
    if (!newSubtaskTitle.trim()) {
      return;
    }

    try {
      const payload = {
        parentBug: currentRow?._id,
        summary: newSubtaskTitle,
        type: "BUG",
        project: currentRow?.project?._id || id,
        bugStatus: currentRow?.bugStatus?._id,
      };

      const result = await createBug(payload).unwrap();

      refreshColumnData(currentRow?.id);
      refetchSubtask();
      toast.success(t("Subtask created successfully"));
      setNewSubtaskTitle("");
      setIsAddingSubtask(false);
    } catch (error) {
      console.error("Error creating subtask:", error);
      toast.error(t("Failed to create subtask"));
    }
  };

  const handleSubtaskKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      handleAddSubtask();
    } else if (e.key === "Escape") {
      setIsAddingSubtask(false);
      setNewSubtaskTitle("");
    }
  };

  const handleSubtaskTextChange = (e) => {
    setNewSubtaskTitle(e.target.value);
  };

  const handleDelete = () => {
    if (!bugToDelete) return;

    deleteBug(bugToDelete?._id)
      .unwrap()
      .then(() => {
        setIsDeleteReply(false);
        setBugToDelete(null);
        toast.success(t("Bug deleted successfully"));
        refreshColumnData(currentRow?.id);
      })
      .catch((error) => {
        console.error("Delete error:", error);
        toast.error(
          t("Failed to delete bug:", {
            error: error?.data?.message || t("Unknown error"),
          })
        );
      });
  };

  const customHandleAction = (data, action, type) => {
    if (data._id === ADD_SUBTASK_ROW_ID) return;

    if (action === "edit") {
      if (type === "subTask") {
        // Handle subtask editing differently if needed
      } else {
        if (isFullscreen) setIsFullscreen(false);
        handleOpenModal(data._id);
      }
    } else if (action === "delete") {
      setBugToDelete(data);
      setIsDeleteReply(true);
    } else {
      handleAction(data, action, type);
    }
  };

  const handleNameClick = (event, taskId, currentName) => {
    if (taskId === ADD_SUBTASK_ROW_ID) return;

    event.stopPropagation();
    setEditingNameId(taskId);
    setEditedName(currentName);
  };

  const handleNameChange = (event) => {
    setEditedName(event.target.value);
  };

  const saveEditedName = async () => {
    if (!editedName.trim()) {
      toast.error(t("Name cannot be empty"));
      return;
    }

    try {
      const task = listData.find((task) => task._id === editingNameId);
      if (!task) {
        toast.error(t("Task not found"));
        return;
      }

      const { data } = await fetchBugDetail({
        bugId: task._id,
        withDetails: true,
      });
      const bugData = data?.data?.decryptedData || task;

      const updatePayload = {
        bugId: task._id,
        summary: editedName.trim(),
        description: bugData.description,
        severity: bugData.severity,
        assignTo: bugData.assignTo?.map((assignee) => assignee._id) || [],
        pageUrl: bugData.pageUrl,
        updatedAt: new Date(),
        bugStatus: bugData.bugStatus?._id,
        screenShots: bugData.screenShots,
        attachments: bugData.attachments,
        tags: bugData.tags,
        createdAt: bugData.createdAt,
        type: bugData.type,
        platform: bugData.platform,
        jiraConfig: {
          projectKey: bugData.jiraConfig?.projectKey,
          projectId: bugData.project?._id || bugData?.project,
          projectName: bugData.projectName,
          jiraDatas: bugData.jiraConfig?.jiraDatas,
        },
        dueDate: bugData.dueDate,
        estimation: bugData.estimation,
        project: bugData.project?._id,
      };

      await updateBug(updatePayload)
        .unwrap()
        .then(() => {
          toast.success(t("Task name updated successfully"));
          refreshColumnData(currentRow?.id, "", paginationModel);
          if (type === "subTask") {
            refetchSubtask();
          }
        })
        .catch((error) => {
          console.error("Update error:", error);
          toast.error(
            t("Failed to update task name:", {
              error: error?.data?.message || t("Unknown error"),
            })
          );
        });
      handlePageChange(currentRow?.id, pagination, "list");
    } catch (error) {
      console.error("Error updating task name:", error);
      toast.error(t("An unexpected error occurred"));
    } finally {
      setEditingNameId(null);
    }
  };

  const handleNameKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      saveEditedName();
    } else if (event.key === "Escape") {
      event.preventDefault();
      setEditingNameId(null);
    }
  };

  const handleClickEdit = async (event, taskId, field) => {
    if (taskId === ADD_SUBTASK_ROW_ID) return;

    event.stopPropagation();
    setEditingTaskId(taskId);
    setEditingField(field);
    setAnchorEl(event.currentTarget);

    if (
      field === "assignTo" ||
      field === "dueDate" ||
      field === "bugStatus" ||
      field === "severity" ||
      field === "type"
    ) {
      try {
        const { data } = await fetchBugDetail({
          bugId: taskId,
          withDetails: true,
        });
        const task = data?.data?.decryptedData;
        if (task && task.assignTo) {
          setSelectedAssignees(
            users.filter((user) =>
              task.assignTo.some((assignee) => assignee._id === user._id)
            )
          );
        } else {
          setSelectedAssignees([]);
        }
      } catch (error) {
        console.error("Error fetching task details:", error);
      }
    }
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setEditingTaskId(null);
    setEditingField(null);
  };

  const handleAssigneeChange = (newAssignees) => {
    setSelectedAssignees(newAssignees);
    if (editingTaskId) {
      const assigneeIds = newAssignees.map((user) => user._id);
      handleUpdateField(assigneeIds);
    }
    handleClosePopover();
  };

  const handleUpdateField = async (value) => {
    try {
      const task = listData.find((task) => task._id === editingTaskId);

      if (!task) {
        toast.error(t("Task not found"));
        handleClosePopover();
        return;
      }

      const bugData = editingBugDetail?.data?.decryptedData || task;

      // Store the previous bug status to compare for changes
      const prevBugStatus = bugData.bugStatus?._id;

      let updatePayload = {
        bugId: task._id,
        summary: bugData.summary,
        description: bugData.description,
        severity: bugData.severity,
        assignTo: bugData.assignTo?.map((assignee) => assignee._id) || [],
        pageUrl: bugData.pageUrl,
        updatedAt: new Date(),
        bugStatus: bugData.bugStatus?._id,
        screenShots: bugData.screenShots,
        attachments: bugData.attachments,
        tags: bugData.tags,
        createdAt: bugData.createdAt,
        type: bugData.type,
        platform: bugData.platform,
        jiraConfig: {
          projectKey: bugData.jiraConfig?.projectKey,
          projectId: bugData?.project?._id || bugData?.project,
          projectName: bugData.projectName,
          jiraDatas: bugData.jiraConfig?.jiraDatas,
        },
        dueDate: bugData.dueDate,
        estimation: bugData.estimation,
        project: bugData.project?._id,
      };

      switch (editingField) {
        case "severity":
          updatePayload.severity = value;
          break;
        case "bugStatus":
          updatePayload.bugStatus = value;
          break;
        case "dueDate":
          updatePayload.dueDate = value;
          break;
        case "assignTo":
          updatePayload.assignTo = value;
          break;
        case "type":
          updatePayload.type = value;
          break;
        default:
          break;
      }

      await updateBug(updatePayload)
        .unwrap()
        .then(async () => {
          // Call updateBugIndex if bugStatus changed
          if (editingField === "bugStatus" && value !== prevBugStatus) {
            try {
              await updateBugIndex({
                // crossColumn: "true",
                // items: {
                //   _id: task._id,
                //   boardIndex: 0,
                //   bugStatus: value,
                //   project: id,
                // },
                lexo: true,

                project: id,
                nextId: columnData[value]?.tasks?.[0]?._id || null,
                prevId: null,
                movedId: task._id,
              }).unwrap();
            } catch (error) {
              console.error("Error updating bug index:", error);
            }
          }

          toast.success(
            `${
              editingField === "bugStatus"
                ? "Status"
                : editingField === "severity"
                ? "Priority"
                : editingField === "assignTo"
                ? "Assignee"
                : editingField === "type"
                ? "Type"
                : "Due Date"
            } ${t("updated successfully")}`
          );
          refreshColumnData(currentRow?.id, "", paginationModel);
          // Also refresh the destination column when status changes
          if (editingField === "bugStatus" && value !== prevBugStatus) {
            refreshColumnData(value);
          }
          if (type === "subTask") {
            refetchSubtask();
          }
        })
        .catch((error) => {
          console.error("Update error:", error);
          toast.error(
            `Failed to update ${
              editingField === "bugStatus"
                ? "status"
                : editingField === "severity"
                ? "priority"
                : editingField === "assignTo"
                ? "assignee"
                : editingField === "type"
                ? "type"
                : "due date"
            }: ${error?.data?.message || "Unknown error"}`
          );
        });
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error(t("An unexpected error occurred"));
    } finally {
      handleClosePopover();
    }
  };

  const handleAssigneeBlur = () => {
    // This function is kept for compatibility
  };

  const handlePageUpdate = (pagination: { pageSize: number; page: number }) => {
    setPaginationModel(pagination);
    if (type === "task") {
      handlePageChange(currentRow?.id, pagination, "list");
    }
  };

  const columns: GridColDef[] = [
    {
      field: "summary",
      headerName: t("Name"),
      flex: 1.5,
      renderCell: (params) => {
        const rowType = params?.row?.type || "";
        const typeInfo = rowType
          ? typeToIconMapping[rowType]
          : typeToIconMapping.TASK;

        if (params.id === ADD_SUBTASK_ROW_ID) {
          if (type === "task") {
            return (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setCreateTask(true));
                  if (groupBy === "Status") {
                    dispatch(setSelectedColumnStatus(currentRow?.id));
                  }
                }}
                sx={{
                  height: "fit-content",
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  color: "#6B7888",
                  textTransform: "none",
                  fontSize: "13px",
                  minHeight: "27px",
                  p: 1,
                  justifyContent: "flex-start",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                  },
                }}
                startIcon={<Add />}
              >
                {t("Add item")}
              </Button>
            );
          }

          if (type === "subTask") {
            if (!isAddingSubtask) {
              return (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAddingSubtask(true);
                  }}
                  sx={{
                    height: "fit-content",
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    color: "#6B7888",
                    textTransform: "none",
                    fontSize: "13px",
                    minHeight: "27px",
                    p: 1,
                    justifyContent: "flex-start",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                    },
                  }}
                  startIcon={<Add />}
                >
                  {t("Add subtask")}
                </Button>
              );
            } else {
              return (
                <ClickAwayListener
                  onClickAway={() => {
                    setIsAddingSubtask(false);
                    setNewSubtaskTitle("");
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "white",
                      borderRadius: 1,
                      p: 1,
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TextField
                      size="small"
                      value={newSubtaskTitle}
                      onChange={handleSubtaskTextChange}
                      onKeyDown={handleSubtaskKeyDown}
                      placeholder={t("Enter subtask name...")}
                      autoFocus
                      inputProps={{
                        onKeyDown: (e) => e.stopPropagation(),
                        onClick: (e) => e.stopPropagation(),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                        },
                        "& .MuiInputBase-input": {
                          px: 1,
                          py: 0.5,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSubtask();
                        }}
                      >
                        {t("Add")}
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsAddingSubtask(false);
                          setNewSubtaskTitle("");
                        }}
                      >
                        {t("Cancel")}
                      </Button>
                    </Box>
                  </Box>
                </ClickAwayListener>
              );
            }
          }
        }

        const isEditing = editingNameId === params.row._id;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              cursor: "pointer",
              position: "relative",
              overflow: "visible",
              height: "100%",
            }}
          >
            {isEditing ? (
              <ClickAwayListener onClickAway={saveEditedName}>
                <TextField
                  size="small"
                  value={editedName}
                  onChange={handleNameChange}
                  inputRef={nameInputRef}
                  autoFocus
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                    "& .MuiInputBase-input": {
                      px: 1,
                      py: 0.5,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    handleNameKeyDown(e);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  inputProps={{
                    style: { fontSize: "0.875rem", fontWeight: 500 },
                  }}
                />
              </ClickAwayListener>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                {rowType && (
                  <Box
                    component="span"
                    sx={{
                      width: 16,
                      height: 16,
                      mr: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: typeInfo?.bgcolor,
                      borderRadius: "3px",
                      color: "white",
                    }}
                  >
                    {typeInfo?.icon}
                  </Box>
                )}
                {params?.row?.bugKey && (
                  <span
                    onClick={() => handleOpenModal(params?.row?._id)}
                    style={{ marginRight: "4px", color: "#1976D2" }}
                  >
                    {params?.row?.bugKey}:
                  </span>
                )}
                <Typography
                  title={params?.value?.length > 30 ? params?.value : ""}
                  onClick={(e) => {
                    if (!isEditing && params.id !== ADD_SUBTASK_ROW_ID) {
                      handleNameClick(e, params.row._id, params.value);
                    }
                  }}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params?.value}
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: "type",
      headerName: t("Type"),
      flex: 0.6,
      renderCell: (params) => {
        if (params.id === ADD_SUBTASK_ROW_ID) return null;

        const itemType = availableItemTypes.find((type) => {
          if (params?.row?.type === "BUG") {
            return type?.code === "DEFECT";
          }
          return type.code === params?.row?.type;
        });

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              position: "relative",
              height: "100%",
              gap: 1,
            }}
            onClick={(e) => handleClickEdit(e, params.row._id, "type")}
          >
            <Typography variant="body2">
              {itemType?.label || params?.row?.type || "-"}
            </Typography>
          </Box>
        );
      },
      headerAlign: "left",
    },
    {
      field: "assignTo",
      headerName: t("Assignee"),
      flex: 0.7,
      sortComparator: (v1, v2) => {
        if (!v1 || v1.length === 0) {
          if (!v2 || v2.length === 0) return 0;
          return 1;
        }
        if (!v2 || v2.length === 0) return -1;

        const name1 = v1[0]?.fullName?.toLowerCase() || "";
        const name2 = v2[0]?.fullName?.toLowerCase() || "";

        return name1.localeCompare(name2);
      },
      renderCell: (params) => {
        if (params.id === ADD_SUBTASK_ROW_ID) return null;

        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              height: "100%",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={(e) => handleClickEdit(e, params.row._id, "assignTo")}
          >
            <Box
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              {params?.formattedValue?.length === 0 && (
                <PersonAddOutlined
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
              )}
              {params?.formattedValue?.slice(0, 3)?.map((item, index) => (
                <Tooltip
                  id="snr-ai-userList-tooltip"
                  key={item._id}
                  title={item.fullName}
                  arrow
                >
                  <Avatar
                    key={item._id}
                    sx={{
                      backgroundColor:
                        colorPalette[index % colorPalette.length],
                      marginRight: "-4px",
                      cursor: "pointer",
                      position: "relative",
                      zIndex: 10 - index,
                      width: "25px",
                      height: "25px",
                      fontSize: "10px",
                      fontWeight: 500,
                      border: "1px solid white",
                    }}
                  >
                    {getInitials(item?.fullName)}
                  </Avatar>
                </Tooltip>
              ))}
              {params?.formattedValue?.length > 3 && (
                <Avatar
                  sx={{
                    backgroundColor: "#ccc",
                    marginRight: 1,
                    cursor: "pointer",
                    width: "30px",
                    height: "30px",
                    fontSize: "12px",
                    border: "1px solid #fff",
                    fontWeight: 500,
                  }}
                >
                  +{params?.formattedValue?.length - 3}
                </Avatar>
              )}
            </Box>
          </Box>
        );
      },
      headerAlign: "left",
    },
    {
      field: "dueDate",
      headerName: t("Due Date"),
      flex: 0.7,
      renderCell: (params) => {
        if (params.id === ADD_SUBTASK_ROW_ID) return null;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              position: "relative",
              height: "100%",
            }}
            onClick={(e) => handleClickEdit(e, params.row._id, "dueDate")}
          >
            {params?.row?.dueDate ? (
              <Typography variant="body2">
                {params?.row?.dueDate &&
                  moment(
                    params?.row?.dueDate,
                    "YYYY-MM-DDTHH:mm:ss:SSS"
                  )?.isValid() &&
                  moment(
                    params?.row?.dueDate,
                    "YYYY-MM-DDTHH:mm:ss:SSS"
                  )?.format(dateFormat || "DD MMM, YYYY")}
              </Typography>
            ) : (
              <CalendarToday
                sx={{ fontSize: "small", color: "text.secondary" }}
              />
            )}
          </Box>
        );
      },
      headerAlign: "left",
    },
    {
      field: "severity",
      headerName: t("Priority"),
      flex: 0.7,
      renderCell: (params) => {
        if (params.id === ADD_SUBTASK_ROW_ID) return null;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              position: "relative",
              height: "100%",
              gap: 1,
            }}
            onClick={(e) => handleClickEdit(e, params.row._id, "severity")}
          >
            {getSeverityIcon(params?.row?.severity)}
            <Typography
              variant="body2"
              color={severityColorMap[params?.row?.severity] || "transparent"}
            >
              {params?.row?.severity ?? "-"}
            </Typography>
          </Box>
        );
      },
      headerAlign: "left",
    },
    {
      field: "bugStatus",
      headerName: t("Status"),
      flex: 0.7,
      renderCell: (params) => {
        if (params.id === ADD_SUBTASK_ROW_ID) return null;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              position: "relative",
              height: "100%",
            }}
            onClick={(e) => handleClickEdit(e, params.row._id, "bugStatus")}
          >
            <Typography variant="body2">
              {params?.row?.bugStatus?.status ?? "-"}
            </Typography>
          </Box>
        );
      },
      headerAlign: "left",
    },
    {
      field: "commentCount",
      headerName: t("Comments"),
      flex: 0.4,
      renderCell: (params) => {
        if (params.id === ADD_SUBTASK_ROW_ID) return null;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              height: "100%",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={(e) => handleClickEdit(e, params.row._id, "commentCount")}
          >
            <ChatBubbleOutline sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ lineHeight: 1 }}>
              {params?.row?.commentCount || 0}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.5,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      renderHeader: () => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            {type === "task" && (
              <IconButton
                size="small"
                onClick={handleFullscreenToggle}
                sx={{
                  minWidth: "auto",
                  p: 0.5,
                  color: isFullscreen ? "primary.main" : "text.secondary",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
                title={
                  isFullscreen ? t("Exit Fullscreen") : t("Enter Fullscreen")
                }
              >
                {!isFullscreen && (
                  <OpenInFullIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                )}
              </IconButton>
            )}
          </Box>
        );
      },
      renderCell: (params) => {
        if (params.id === ADD_SUBTASK_ROW_ID) return null;

        const actionList: AnyObject[] = [
          { label: t("Edit"), value: "edit" },
          { label: t("Delete"), value: "delete" },
        ];

        return (
          <Box pt={1}>
            <TableAction
              // @ts-ignore
              actionList={
                type === "subTask"
                  ? actionList.filter((item) => item.value !== "edit")
                  : actionList
              }
              dataItem={params?.row}
              onAction={(data, action) =>
                customHandleAction(data, action, type)
              }
            />
          </Box>
        );
      },
    },
  ];

  const selectedTask =
    listData.find((task) => task._id === selectedTaskId) || null;

  const editingTask =
    listData.find((task) => task._id === editingTaskId) || null;

  const fullscreenModalStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 9999,
    backgroundColor: "background.paper",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  const DataGridComponent = (
    <DataGridPro
      getRowId={(row) => row?._id}
      columns={columns}
      rows={listData || []}
      loading={
        updatingBug ||
        isDeleteLoading ||
        isCreatingBug ||
        loadingStatuses?.[currentRow?.id]?.isInitialLoading ||
        loadingStatuses?.[currentRow?.id]?.isLoadingMore ||
        loadingSubtask ||
        fetchingSubtask ||
        isFetchingEditingBug
      }
      rowSelection={false}
      // rowHeight={45}
      localeText={customLocaleText}
      getDetailPanelHeight={() => "auto"}
      hideFooter={type === "subTask" ? true : false}
      disableColumnMenu
      // @ts-ignore
      isRowExpandable={({ row }) => hasSubtasks(row)}
      style={{
        border: "none",
        height: isFullscreen ? "calc(100vh - 100px)" : "auto",
      }}
      pagination={type === "task" ? true : false}
      getDetailPanelContent={({ row }) => {
        if (!shouldShowDetailPanel(row)) return null;
        return (
          <NestedTaskList
            currentRow={row}
            handleAction={handleAction}
            type="subTask"
            pagination={pagination}
            handlePageChange={() => {}}
            loadingStatuses={{
              ...loadingStatuses,
            }}
            projectDetails={projectDetails}
          />
        );
      }}
      slots={{
        noRowsOverlay: () => (
          <GridOverlay>
            <div>{type === "task" ? t("No Tasks") : t("No Subtasks")}</div>
          </GridOverlay>
        ),
        detailPanelExpandIcon: KeyboardArrowRight,
        detailPanelCollapseIcon: KeyboardArrowDown,
        pagination: () =>
          type === "task" ? (
            <Pagination
              total={pagination?.[currentRow?.id]?.total || 10}
              paginationModel={paginationModel}
              setPaginationModel={handlePageUpdate}
            />
          ) : null,
      }}
    />
  );

  return (
    <>
      {isFullscreen && type === "task" ? (
        <Modal
          open={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          sx={{
            "& .MuiModal-backdrop": {
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            },
          }}
        >
          <Paper sx={fullscreenModalStyle}>
            {/* Fullscreen header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                minHeight: "60px",
                backgroundColor: "background.paper",
              }}
            >
              <Typography variant="subtitle1">
                {t("Task List")} - {t("Fullscreen View")}
              </Typography>
              <IconButton
                onClick={handleFullscreenToggle}
                sx={{
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
                title={t("Exit Fullscreen")}
              >
                <CloseFullscreenIcon
                  sx={{ color: "text.primary" }}
                  fontSize="small"
                />
              </IconButton>
            </Box>

            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                p: 2,
              }}
            >
              {DataGridComponent}
            </Box>
          </Paper>
        </Modal>
      ) : (
        <Box ml={3}>{DataGridComponent}</Box>
      )}

      <EditPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        fieldType={editingField}
        editingTask={editingTask}
        onUpdate={handleUpdateField}
        theme={theme}
        t={t}
        severityOptions={severityOptions}
        statusOptions={availableStatus}
        isLoadingStatusLabel={isLoadingStatusLabel}
        users={users}
        selectedAssignees={selectedAssignees}
        handleAssigneeChange={handleAssigneeChange}
        handleAssigneeBlur={handleAssigneeBlur}
        isUpdating={updatingBug}
        typeOptions={availableItemTypes}
      />

      {isDeleteReply && (
        <DeleteConfirmModal
          open={isDeleteReply}
          onClose={() => {
            setIsDeleteReply(false);
            setBugToDelete(null);
          }}
          onConfirm={handleDelete}
          confirmMessage={`${t("Are you sure you want to delete this")} ${
            bugToDelete?.type || t("task")
          }?`}
          loading={isDeleteLoading}
          title={t("Delete Task")}
          okText={t("Ok")}
        />
      )}
      {selectedTask && isModalOpen && (
        <TaskUpdateDrawer
          open={isModalOpen}
          onClose={handleClose}
          setBugId={() => {}}
          setDrawerOpen={() => {}}
          bugId={selectedTaskId}
          users={companyMemberData?.data?.decryptedData || []}
          createdBy={selectedTask?.createdBy?.fullName || ""}
          setCreatedBy={() => {}}
          refreshColumn={() => refreshColumnData(currentRow?.id)}
        />
      )}
    </>
  );
};

export default NestedTaskList;
